import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as uf from "../../../../common/unfathomable";
import { ActionDialog } from "./ActionDialog";
import { Announcer } from "./Announcer";
import { DebugPanel } from "./DebugPanel";
import { DeepOneCaption, useDeepOnePlayback } from "./DeepOnePlayback";
import { roundLabel } from "./Panels";
import { DecisionPanel, autoPassPrefs, windowKey } from "./DecisionPanel";
import { DragBoundsCtx, DragPanel } from "./DragPanel";
import { ConfirmButton, HudMenu, kHostTip, PlayersHud, StatusHud } from "./Hud";
import { cardsOfGroup } from "./ResponseOptions";
import { ActivityFeed, CheckPanel, DamageReveal, DieRoll, GameOverModal, MythosSlot, TurnBanner, UndoVote } from "./Overlays";
import { HandBar, LogPanel } from "./Panels";
import { SpaceInfoPanel } from "./SpaceInfoPanel";
import { Scene3D, type MoodOverride } from "./three/Scene3D";
import css from "./Unfathomable.module.css";
import type { UnfathomableConnection } from "./useUnfathomableRoom";

const kStepZh: Record<string, string> = { setup: "准备", receiveSkills: "获得技能", action: "行动", mythos: "神话", discard: "弃牌" };

export function GameTable({ conn, msg, artOk }: { conn: UnfathomableConnection; msg: uf.RoomMessage; artOk: boolean }) {
  // Animation debugging: fake events are merged into the view locally (fractional seq numbers
  // slot them between real events, so nothing is skipped or replayed).
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugEvents, setDebugEvents] = useState<uf.EventEntry[]>([]);
  const [moodOverride, setMoodOverride] = useState<MoodOverride | null>(null);
  const [bolt, setBolt] = useState(0);
  const [viewReset, setViewReset] = useState(0);
  const realView = msg.view!;
  const view = useMemo(() => {
    if (debugEvents.length === 0) return realView;
    const events = [...realView.events, ...debugEvents].sort((a, b) => a.seq - b.seq);
    return { ...realView, events };
  }, [realView, debugEvents]);
  const debugSeq = useRef(0);
  const emitDebug = (evs: uf.GameEvent[]) => {
    const realMax = realView.events.at(-1)?.seq ?? 0;
    // Strictly increasing, between the last real event and the next one (rapid clicks included).
    const fresh = evs.map(ev => {
      debugSeq.current = Math.max(debugSeq.current, realMax) + 0.0005;
      return { seq: debugSeq.current, ev };
    });
    setDebugEvents(d => [...d, ...fresh].slice(-40));
  };
  const [inspect, setInspect] = useState<uf.SpaceId | null>(null);
  // Full-screen notices first: windows asking for a decision wait until they have played.
  const [announcing, setAnnouncing] = useState(false);
  // Deep One activations replay one by one on the table; decisions also wait for that.
  const deepOnes = useDeepOnePlayback(view);
  const holding = announcing || deepOnes.busy;
  const stageRef = useRef<HTMLDivElement>(null);
  const lobby = msg.lobby;
  const d = view.decision;
  const [selected, setSelected] = useState<string[]>([]);
  const [showGameOver, setShowGameOver] = useState(true);
  /** Debug: preview the end screen with a made-up result (local only; its "end" button just closes it). */
  const [previewEnd, setPreviewEnd] = useState<uf.GameOver | null>(null);
  // A game that ends while we watch: let the ending play first (a loss sinks the ship, ~11 s).
  // A page opened on a finished game shows the result at once.
  const [endingDone, setEndingDone] = useState(!!view.gameOver);
  const over = view.gameOver;
  useEffect(() => {
    if (!over || endingDone) return;
    const t = setTimeout(() => setEndingDone(true), over.humansWin ? 1500 : 11500);
    return () => clearTimeout(t);
  }, [over, endingDone]);
  const [logOpen, setLogOpen] = useState(false);
  const [handOpen, setHandOpen] = useState(true);
  useEffect(() => { setSelected([]); }, [d?.id]);
  // The action dialog reopens for every new action decision.
  const [actionOpen, setActionOpen] = useState(true);
  useEffect(() => { if (d?.kind === "action") setActionOpen(true); }, [d?.id, d?.kind]);
  // In a response window: the card ability being used (its play area is open).
  const [respKey, setRespKey] = useState<string | null>(null);
  useEffect(() => { setRespKey(null); }, [d?.id]);
  useEffect(() => { if (respKey) setHandOpen(true); }, [respKey]);
  // Card decisions need the hand: unfold it.
  useEffect(() => { if (d?.mine && d.options.some(o => o.cardUid !== undefined)) setHandOpen(true); }, [d?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = useCallback((choice: string[]) => {
    if (!d) return;
    conn.answer(d.id, choice);
  }, [conn, d]);

  // Auto-pass response windows the player opted out of.
  useEffect(() => {
    if (d?.mine && d.kind === "window" && autoPassPrefs()[windowKey(d.prompt)]) {
      const t = setTimeout(() => conn.answer(d.id, ["pass"]), 300);
      return () => clearTimeout(t);
    }
  }, [d, conn]);

  // Spaces highlighted for the pending decision. Not for actions: moving is done only from the
  // action panel (its mini map), never by clicking the board.
  const targets = useMemo(() => {
    const m = new Map<uf.SpaceId, string>();
    if (d?.mine && d.kind !== "action") for (const o of d.options) if (o.space && !m.has(o.space)) m.set(o.space, o.label);
    return m;
  }, [d]);

  // Hand cards that are options of the pending decision (none while notices or Deep Ones play:
  // the decision is not on screen yet, so its cards must not be clickable either).
  const selectable = useMemo(() => {
    if (holding) return new Map<number, string>();
    if (d?.mine && d.kind === "window") return cardsOfGroup(d.options, view.hand, respKey);
    const m = new Map<number, string>();
    if (d?.mine) for (const o of d.options) if (o.cardUid !== undefined) m.set(o.cardUid, o.id);
    return m;
  }, [d, view.hand, respKey, holding]);
  const toggleCard = (id: string) => {
    if (!d || holding) return;
    // Response abilities use exactly one card: clicking another card swaps it.
    if (d.kind === "window") return setSelected(prev => (prev[0] === id ? [] : [id]));
    if (d.min === 1 && d.max === 1 && d.kind !== "cards" && d.kind !== "contribute") return answer([id]);
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < d.max ? [...prev, id] : prev);
  };

  const downloadBackup = async () => {
    const b = await conn.requestBackup();
    if (!b) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(b)], { type: "application/json" }));
    a.download = `unfathomable-${lobby.roomName}-T${view.turnNumber}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const downloadTranscript = async () => {
    const t = await conn.requestTranscript();
    if (!t) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([t], { type: "text/markdown;charset=utf-8" }));
    a.download = `unfathomable-${lobby.roomName}-T${view.turnNumber}-完整记录.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const cur = view.players[view.current];
  const isHost = lobby.hostSlot === lobby.yourSlot && lobby.yourSlot !== null;
  const hostSeat = lobby.seats.find(s => s.slot === lobby.hostSlot)?.gameSeat ?? null;
  const claimable = lobby.yourSlot === null ? lobby.seats.filter(s => s.gameSeat !== null && s.vacant && s.name) : [];
  // During a skill check its decisions live inside the check window (one window, no overlap).
  // Only while the check is still running: after its result, later decisions (e.g. a Deep One
  // attack from the same mythos card) are not part of it.
  const decisionInCheck = !!view.check && view.check.result === null && !!d && d.kind !== "action";
  const showDecision = !holding && d && !decisionInCheck && !(d.mine && d.kind === "action" && actionOpen);
  const decisionPanel = (
    <DecisionPanel view={view} selected={selected} setSelected={setSelected} onAnswer={answer} onOpenActions={() => setActionOpen(true)} artOk={artOk}
      respKey={respKey} setRespKey={setRespKey} />
  );

  return (
    <DragBoundsCtx.Provider value={stageRef}>
    <div className={css.stage} ref={stageRef}>
      <Scene3D view={deepOnes.sceneView} actSpace={deepOnes.act?.space ?? null} artOk={artOk} targets={targets} onSpace={setInspect} moodOverride={moodOverride} bolt={bolt} viewReset={viewReset} />

      {/* top: status, turn, menu */}
      <div className={css.hudLeftColumn}>
        <DragPanel className={css.leftColumnInner}>
          <StatusHud view={view} artOk={artOk} />
          <PlayersHud view={view} artOk={artOk} hostSeat={hostSeat} />
        </DragPanel>
      </div>
      <div className={css.hudTopCenter}>
        <DeepOneCaption act={deepOnes.act} caption={deepOnes.caption} />
        <div className={css.turnPill}>
          {roundLabel(view.turnNumber, view.players.length)} · <b>{cur?.name}</b>（{uf.kCharacters[cur.character].name}）· {kStepZh[view.step] ?? view.step}
          {view.step === "action" ? ` · 剩余行动 ${view.actionsLeft}` : ""}
        </div>
        {view.you === null && <span className={css.badge}>观战中</span>}
        {claimable.map(s => (
          <button key={s.slot} className={`${css.btn} ${css.btnSmall}`} onClick={() => conn.send("claimSlot", { slot: s.slot })}>认领 {s.name} 的座位</button>
        ))}
        {lobby.waitingOffline.length > 0 && isHost && (
          <button className={`${css.btn} ${css.btnSmall}`} title="等待的玩家都已离线；以「不响应 / 不加牌 / 结束行动 / 第一个选项」代为选择"
            onClick={() => conn.send("forceDefault")}>⏭ 替离线的 {lobby.waitingOffline.join("、")} 做默认选择</button>
        )}
        {lobby.options.testMode && isHost && (
          <>
            <button className={`${css.btn} ${css.btnSmall}`} onClick={() => conn.send("pauseBots", { paused: !lobby.botsPaused })}>
              {lobby.botsPaused ? "▶ 继续机器人" : "⏸ 暂停机器人"}
            </button>
            {lobby.botsPaused && <button className={`${css.btn} ${css.btnSmall}`} title="让正在等待的机器人做出这一步选择" onClick={() => conn.send("stepBots")}>⏭ 单步</button>}
          </>
        )}
      </div>
      <div className={css.hudTopRight}>
        <button className={`${css.btn} ${css.btnSmall}`} title="回到默认的全局视角" onClick={() => setViewReset(n => n + 1)}>🎥 重置视角</button>
        <button className={`${css.btn} ${css.btnSmall}`} onClick={() => setLogOpen(o => !o)}>📜 日志{logOpen ? " ▸" : ""}</button>
        <HudMenu>
          <div className={css.menuTitle}>{lobby.roomName}</div>
          <div className={css.menuNote} title={kHostTip}>👑 房主：{hostSeat !== null ? `${view.players[hostSeat].name}${isHost ? "（你）" : ""}` : "暂无（没有在线玩家）"}</div>
          <button className={`${css.btn} ${css.btnSmall}`} onClick={downloadBackup}>💾 下载存档</button>
          {lobby.options.testMode && <button className={`${css.btn} ${css.btnSmall}`} onClick={downloadTranscript}>📜 下载完整记录</button>}
          {(isHost || view.gameOver || lobby.hostSlot === null) && (
            <ConfirmButton className={`${css.btn} ${css.btnSmall} ${css.btnDanger}`} label="结束本局"
              confirmLabel="⚠ 再点一次：放弃本局回到大厅（建议先下载存档）" onConfirm={() => conn.send("reset")} />
          )}
          <button className={`${css.btn} ${css.btnSmall}`} onClick={() => setDebugOpen(true)}>🛠 动画调试</button>
          <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} onClick={conn.leave}>离开房间</button>
        </HudMenu>
      </div>


      <MythosSlot view={view} artOk={artOk} />
      <DamageReveal view={view} artOk={artOk} />
      <ActivityFeed view={view} />

      {/* bottom: decision + hand */}
      <div className={css.hudBottom}>
        {showDecision && (
          <div className={css.decisionDock}>
            <DragPanel>{decisionPanel}</DragPanel>
          </div>
        )}
        <div className={css.handDock}>
          <button className={css.handToggle} onClick={() => setHandOpen(o => !o)}>
            {handOpen ? "收起手牌 ▾" : `手牌 ${view.hand.length} 张 ▴`}
          </button>
          <AnimatePresence initial={false}>
            {handOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
                <HandBar view={view} artOk={artOk} selectable={selectable} selected={selected} onToggle={toggleCard} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {logOpen && (
          <motion.div className={css.logDrawer} initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 260, damping: 30 }}>
            <LogPanel view={view} onProposeUndo={(decisionId, reshuffle) => conn.send(uf.Api.kProposeUndo, { decisionId, reshuffle } as uf.Api.ProposeUndoRequest)} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Plain mount/unmount: swapping keys inside AnimatePresence could leave an invisible panel behind. */}
      {inspect && <SpaceInfoPanel key={inspect} view={view} sp={inspect} onClose={() => setInspect(null)} onAnswer={answer} />}
      {debugOpen && (
        <DebugPanel view={view} emit={emitDebug} mood={moodOverride} setMood={setMoodOverride} onBolt={() => setBolt(b => b + 1)} onClose={() => setDebugOpen(false)}
          onPreviewEnd={win => setPreviewEnd({
            reason: win ? "arrived" : "damage", detail: win ? "亚特兰蒂卡号抵达了最终目的地" : "船上同时有 6 个受损空间", humansWin: win,
            winners: view.players.filter(p => (p.seat === view.you) === win).map(p => p.seat),
            loyalties: view.players.map(p => ({ seat: p.seat, cards: [{ uid: 9000 + p.seat, type: p.seat === view.you || win ? "human" : "hybrid" }] })),
          })} />
      )}

      <ActionDialog view={view} open={actionOpen && !holding} setOpen={setActionOpen} onAnswer={answer} artOk={artOk} />
      <TurnBanner view={view} />
      {!holding && <CheckPanel view={view} artOk={artOk}>{decisionInCheck ? decisionPanel : null}</CheckPanel>}
      <DieRoll view={view} />
      <Announcer view={view} artOk={artOk} onBusy={setAnnouncing} />
      <UndoVote lobby={lobby} onVote={approve => conn.send(uf.Api.kVoteUndo, { approve } as uf.Api.VoteUndoRequest)} />
      {view.gameOver && showGameOver && endingDone && (
        <GameOverModal view={view} artOk={artOk} onClose={() => setShowGameOver(false)} onEnd={() => conn.send("reset")} onDownload={downloadBackup} />
      )}
      {previewEnd && (
        <GameOverModal view={{ ...view, gameOver: previewEnd }} artOk={artOk} onClose={() => setPreviewEnd(null)} onEnd={() => setPreviewEnd(null)} onDownload={downloadBackup} />
      )}
      {view.gameOver && !showGameOver && endingDone && (
        <button className={`${css.btn} ${css.endReopen}`} onClick={() => setShowGameOver(true)}>🏁 查看结局</button>
      )}
    </div>
    </DragBoundsCtx.Provider>
  );
}
